import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Crown, Share2, Users, Zap } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../../utils/time';
import { useConfirm } from '../../../components/ConfirmDialog';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, Tabs, useUrlTab,
  money, num, plural,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, TotalsCheck, Notice,
  DateRange, RefreshButton, MoneyTooltip, codeLabel,
} from '../financeKit';

// Shares are categories, not states, so they need telling apart — muted tints of one family.
const COLOURS = ['#4f46e5', '#64748b', '#818cf8', '#94a3b8', '#3730a3', '#cbd5e1', '#a5b4fc', '#475569'];
const EMPTY = { owners: [], stakeholders: [], per_client_referrals: [], summary: {} };
const TAB_IDS = ['referrals', 'global', 'owners'];

const pctText = (v) => `${num(v, 2)}%`;
const sumOf = (rows, key) => rows.reduce((s, r) => s + (parseFloat(r[key]) || 0), 0);

function ColourDot({ colour }) {
  return <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle shrink-0" style={{ background: colour }} aria-hidden="true" />;
}

// A referrer's row; its button opens the clients (and profit) the share comes from.
function ReferrerRows({ row, colour }) {
  const [open, setOpen] = useState(false);
  const clients = row.clients || [];
  return (
    <>
      <tr className={TR}>
        <td className={TD}>
          <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
            className="-ml-1 inline-flex min-h-[36px] items-center gap-2 rounded-lg px-1 text-left font-medium text-slate-800 hover:text-slate-950">
            {open ? <ChevronDown size={14} className="shrink-0 text-slate-400" aria-hidden="true" /> : <ChevronRight size={14} className="shrink-0 text-slate-400" aria-hidden="true" />}
            <ColourDot colour={colour} />
            <span>{row.name}</span>
            <span className="sr-only">{open ? ', hide clients' : ', show clients'}</span>
          </button>
        </td>
        <td className={TD_NUM}>{plural(clients.length, 'client')}</td>
        <td className={TD_NUM}><MoneyCell value={row.total_amount} strong /></td>
      </tr>
      {open && clients.map((c, ci) => (
        <tr key={c.assignment_id || ci} className="border-b border-slate-100 bg-slate-50/60">
          <td className="py-2 pl-12 pr-4 text-[13px] text-slate-700">
            <div className="flex items-center gap-2">
              <span className="badge-gray">{codeLabel(c.role)}</span>
              <span>{c.client_name}</span>
            </div>
            {c.notes && <p className="mt-0.5 pl-1 text-[12px] text-slate-500">{c.notes}</p>}
          </td>
          <td className="px-4 py-2 text-right text-[12px] text-slate-500 tabular-nums whitespace-nowrap">
            {pctText(c.profit_percentage)} of {money(c.client_profit)} profit
          </td>
          <td className="px-4 py-2 text-right whitespace-nowrap"><MoneyCell value={c.amount} /></td>
        </tr>
      ))}
    </>
  );
}

// Name / share / amount, with a pie above and a totals row.
function ShareTable({ rows, nameLabel, shareLabel }) {
  const pieData = rows.map(r => ({ name: r.name, amount: parseFloat(r.amount || 0) }));
  return (
    <>
      <div className="px-4 pt-4">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85}
              dataKey="amount" nameKey="name" paddingAngle={2} isAnimationActive={false}>
              {pieData.map((_, i) => <Cell key={i} fill={COLOURS[i % COLOURS.length]} />)}
            </Pie>
            <Tooltip content={<MoneyTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className={TABLE_WRAP}>
        <table className={`${TABLE} mt-2`}>
          <thead>
            <tr className={THEAD_ROW}>
              <th scope="col" className={TH}>{nameLabel}</th>
              <th scope="col" className={TH_NUM}>{shareLabel}</th>
              <th scope="col" className={TH_NUM}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className={TR}>
                <td className={TD}>
                  <ColourDot colour={COLOURS[i % COLOURS.length]} />
                  <span className="font-medium text-slate-800">{r.name}</span>
                </td>
                <td className={TD_NUM}>{pctText(r.percentage)}</td>
                <td className={TD_NUM}><MoneyCell value={r.amount} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={TFOOT_ROW}>
              <td className={TD}>Total</td>
              <td className={TD_NUM}>{pctText(sumOf(rows, 'percentage'))}</td>
              <td className={TD_NUM}><MoneyCell value={sumOf(rows, 'amount')} strong /></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

export default function StakeholderReport() {
  const confirm = useConfirm();
  const [data, setData]     = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useUrlTab('tab', 'referrals', TAB_IDS); // 'referrals' | 'global' | 'owners'
  const today = new Date();
  const [from, setFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/stakeholders?from=${from}&to=${to}`);
      setData(r.data || EMPTY);
    } catch (e) {
      setData(EMPTY);
      setError(e.response?.data?.message || "We couldn't load the stakeholder report. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  // Rewrites client and staff rates and recalculates shifts, so it asks first.
  async function runAutoSetup() {
    const ok = await confirm(
      "Client rates are copied from each client's price book and staff rates from their pay group, replacing the rates stored now. Then shifts are recalculated with the new rates. A large account may need more than one run.",
      { title: 'Sync rates and recalculate shifts?', confirmLabel: 'Sync and recalculate' },
    );
    if (!ok) return;
    setSyncing(true);
    try {
      const r = await api.post('/financials/auto-setup', {});
      const left = Number(r.data?.shifts_remaining) || 0;
      toast.success(`Rates synced and ${plural(r.data?.shifts_recalculated, 'shift')} recalculated${left > 0 ? `. ${num(left)} still to do: run it again to continue` : ''}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't sync rates and recalculate shifts.");
    } finally { setSyncing(false); }
  }

  useEffect(() => { load(); }, [load]);

  const s = data.summary || {};
  const referrals = data.per_client_referrals || [];
  const owners = data.owners || [];
  const stakeholders = data.stakeholders || [];
  const totalReferralPool = referrals.reduce((sum, r) => sum + parseFloat(r.total_amount || 0), 0);

  const syncButton = (
    <button type="button" onClick={runAutoSetup} disabled={syncing || loading} className="btn-secondary"
      title="Sync rates from price books and pay groups, then recalculate shifts">
      <Zap size={14} className={syncing ? 'animate-pulse' : ''} aria-hidden="true" />
      {syncing ? 'Syncing…' : 'Sync rates'}
    </button>
  );

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="Stakeholder shares"
        description="How profit and revenue are shared between per-client referrers, global stakeholders and owners."
        actions={(
          <>
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="stakeholders" />
            {syncButton}
            <RefreshButton onClick={load} loading={loading} label="Refresh report" />
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Diagnostic when the range has no revenue */}
          {!loading && !s.total_revenue && (
            <Notice tone="amber" title="No revenue in this range">
              Shifts may not have rates yet. Sync rates copies them from your price books and pay groups, then recalculates the shifts.
            </Notice>
          )}

          {/* Summary — net profit − referral pool = owner pool. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Total revenue" value={loading ? '…' : money(s.total_revenue)} accent="slate"
              sub={loading ? null : 'Across all clients'} />
            <KpiTile label="Net profit" value={loading ? '…' : money(s.net_profit)} accent="slate"
              alert={!loading && Number(s.net_profit) < 0} sub={loading ? null : 'After costs and expenses'} />
            <KpiTile label="Referral pool" value={loading ? '…' : money(s.referral_pool)} accent="slate"
              sub={loading ? null : 'Paid to referrers first'} />
            <KpiTile label="Owner pool" value={loading ? '…' : money(s.owner_pool)} accent="primary"
              sub={loading ? null : 'Net profit less the referral pool'} />
          </div>

          <Tabs ariaLabel="Share type" value={tab} onChange={setTab} tabs={[
            { id: 'referrals', label: 'Per-client referrers', count: loading ? null : referrals.length },
            { id: 'global', label: 'Global stakeholders', count: loading ? null : stakeholders.length },
            { id: 'owners', label: 'Owners', count: loading ? null : owners.length },
          ]} />

          {loading ? (
            <div className="card"><TableSkeleton rows={5} cols={3} /></div>
          ) : (
            <>
              {/* Per-client referrers */}
              {tab === 'referrals' && (
                <SectionCard title={`Per-client referrers · ${referrals.length}`} icon={Share2}>
                  {referrals.length === 0 ? (
                    <EmptyState icon={Share2} title="No referrers assigned to any client"
                      hint="Open a client's profile, go to Referral stakeholders and assign a primary or secondary referrer with their agreed profit %." />
                  ) : (
                    <>
                      <div className={TABLE_WRAP}>
                        <table className={TABLE}>
                          <thead>
                            <tr className={THEAD_ROW}>
                              <th scope="col" className={TH}>Referrer</th>
                              <th scope="col" className={TH_NUM}>Clients</th>
                              <th scope="col" className={TH_NUM}>Earned</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referrals.map((row, i) => (
                              <ReferrerRows key={row.stakeholder_id || i} row={row} colour={COLOURS[i % COLOURS.length]} />
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className={TFOOT_ROW}>
                              <td className={TD}>Total referral pool</td>
                              <td className={TD_NUM} />
                              <td className={TD_NUM}><MoneyCell value={totalReferralPool} strong /></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <p className="px-4 py-2.5 text-xs text-slate-500 border-t border-slate-100">
                        A referrer earns their % of each client's profit, not of revenue. Open a referrer to see the clients it comes from.
                      </p>
                    </>
                  )}
                </SectionCard>
              )}
              {tab === 'referrals' && referrals.length > 0 && (
                <TotalsCheck checks={[{ label: 'Referral pool', computed: totalReferralPool, expected: s.referral_pool }]} />
              )}

              {/* Global stakeholders */}
              {tab === 'global' && (
                <SectionCard title={`Global stakeholders · ${stakeholders.length}`} icon={Users}>
                  <p className="px-4 pt-3 text-[13px] text-slate-500">
                    A global stakeholder gets the same % of all revenue. For a share of one client's profit, use Per-client referrers.
                  </p>
                  {stakeholders.length === 0 ? (
                    <EmptyState icon={Users} title="No global stakeholders set up"
                      hint="Add them under Stakeholders in Financial configuration, with the % of revenue each one receives."
                      action={<Link to="/financials/config" className="btn-secondary">Open financial configuration</Link>} />
                  ) : (
                    <ShareTable rows={stakeholders} nameLabel="Stakeholder" shareLabel="Share of revenue" />
                  )}
                </SectionCard>
              )}

              {/* Owners */}
              {tab === 'owners' && (
                <SectionCard title={`Owners · ${owners.length}`} icon={Crown}>
                  <div className="mx-4 mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-slate-50 px-4 py-2.5 text-[13px] text-slate-600">
                    <span><span className="font-semibold text-slate-800 tabular-nums">{money(s.net_profit)}</span> net profit</span>
                    <span aria-hidden="true">−</span>
                    <span><span className="font-semibold text-slate-800 tabular-nums">{money(s.referral_pool)}</span> referral pool</span>
                    <span aria-hidden="true">=</span>
                    <span><span className="font-semibold text-slate-800 tabular-nums">{money(s.owner_pool)}</span> owner pool</span>
                    <span className="text-slate-500">(owners share this amount)</span>
                  </div>
                  {owners.length === 0 ? (
                    <EmptyState icon={Crown} title="No owners set up"
                      hint="Add them under Owners in Financial configuration, with each owner's % of the owner pool."
                      action={<Link to="/financials/config" className="btn-secondary">Open financial configuration</Link>} />
                  ) : (
                    <ShareTable rows={owners} nameLabel="Owner" shareLabel="Share of owner pool" />
                  )}
                </SectionCard>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
